import React, { useState } from 'react';
import { Zap, Check, Inbox, Mail, Sparkles, FileText } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import ModelSelector from '../../../../../../components/agno/ModelSelector';

interface SignalConfig {
  name: string;
  description?: string;
  trigger: string;
  entity_relation: string;
  filters?: any;
  automation_actions?: {
    actions?: Array<{ type: string; skill_ids?: string[] }>;
    model_key?: string | null;
  };
  selected_fields?: string[];
  editing_signal_id?: string;
  selected_skills?: Array<{ id: string; name: string }>;
}

interface StepProps {
  result: {
    ui_step: string;
    signal_config: SignalConfig;
  };
  onSelect: (value: string, label: string) => void;
  /** Controlled per-signal model key + change handler (wired by SignalChatBuilder). */
  modelKey?: string;
  onModelKeyChange?: (modelKey: string) => void;
}

const SlackIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zm2.521-10.123a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

const DESTINATION_ICONS: Record<string, React.ReactNode> = {
  send_to_slack: <SlackIcon className="h-4 w-4" />,
  send_to_inbox: <Inbox className="h-4 w-4" />,
  send_to_email: <Mail className="h-4 w-4" />,
};

const DESTINATION_LABELS: Record<string, string> = {
  send_to_slack: 'Slack',
  send_to_inbox: 'Inbox',
  send_to_email: 'Email',
};

const SignalSummaryStep: React.FC<StepProps> = ({ result, onSelect, modelKey, onModelKeyChange }) => {
  const [confirmed, setConfirmed] = useState(false);
  const { signal_config: config } = result;
  const effectiveModelKey = modelKey ?? config.automation_actions?.model_key ?? undefined;

  const allActions = config.automation_actions?.actions || [];
  const destinations = allActions.filter((a) => a.type !== 'run_skills').map((a) => a.type);
  const hasAIOutput = allActions.some((a) => a.type === 'run_skills');
  const linkedSkills = config.selected_skills || [];
  const isEditing = !!config.editing_signal_id;

  const handleConfirm = () => {
    if (confirmed) return;
    setConfirmed(true);
    onSelect('confirm', isEditing ? 'Confirm and update this signal' : 'Confirm and create this signal');
  };

  const handleEdit = () => {
    onSelect('edit', 'I want to make changes to the signal');
  };

  if (confirmed) {
    return (
      <div className="w-full max-w-lg p-4 rounded-lg border border-violet-200 bg-violet-50">
        <div className="flex items-center gap-2 text-violet-700">
          <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">{isEditing ? 'Updating your signal…' : 'Creating your signal…'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg border border-gray-200 bg-white rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center gap-2">
        <div className="p-1.5 rounded-md bg-gray-100">
          <Zap className="h-4 w-4 text-gray-500" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{config.name}</p>
          {config.description && (
            <p className="text-xs text-gray-500">{config.description}</p>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="p-4 space-y-4 text-sm">
        {/* Entity & Trigger */}
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">Monitors</p>
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 capitalize">
              {config.entity_relation}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
              {config.trigger === 'usage_based' ? 'Success Metric-based' : 'Event-based'}
            </span>
          </div>
        </div>

        {/* Destinations */}
        {destinations.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">Send via</p>
            <div className="flex flex-wrap gap-1.5">
              {destinations.map((dest) => (
                <div
                  key={dest}
                  className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 bg-gray-50"
                >
                  <span className="text-gray-500">{DESTINATION_ICONS[dest]}</span>
                  <span className="text-xs text-gray-600">{DESTINATION_LABELS[dest] || dest}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Linked Analysis */}
        {(hasAIOutput || linkedSkills.length > 0) && (
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">Linked Analysis</p>
            {linkedSkills.length > 0 ? (
              <div className="space-y-1.5">
                {linkedSkills.map((skill) => (
                  <div
                    key={skill.id}
                    className="flex items-center gap-1.5 px-2 py-1 rounded border border-violet-200 bg-violet-50 w-fit"
                  >
                    <FileText className="h-4 w-4 text-violet-500" />
                    <span className="text-xs text-violet-700">{skill.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-violet-200 bg-violet-50 w-fit">
                <Sparkles className="h-4 w-4 text-violet-500" />
                <span className="text-xs text-violet-700">AI analysis included with each alert</span>
              </div>
            )}
          </div>
        )}

        {/* AI Model */}
        {onModelKeyChange && (
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">AI Model</p>
            <ModelSelector
              surface="signals"
              value={effectiveModelKey}
              onChange={onModelKeyChange}
              className="h-8 rounded border border-gray-200 bg-gray-50"
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-100 space-y-2">
        <button
          type="button"
          onClick={handleConfirm}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors"
        >
          <Check className="h-4 w-4" />
          {isEditing ? 'Update Signal' : 'Create Signal'}
        </button>
        <button
          type="button"
          onClick={handleEdit}
          className="w-full px-4 py-2 rounded-md border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Make Changes
        </button>
      </div>
    </div>
  );
};

export default SignalSummaryStep;
